import { GenieBranding, QRCodeStyle, QRDisplayStyle, QRLabelOption } from '@/types/ui/branding';
import type { PackKitCopy } from '@/types/activation/packKit';
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

// =====================================================================
// Navigation
// =====================================================================

/**
 * Where the activation pack page should return to. Threaded through router
 * location state so a pack opened from inside a Genie sends the user back to
 * that Genie — never ejecting them to the global activation list.
 */
export interface ActivationBackOrigin {
  path: string;
  label: string;
}

// =====================================================================
// Channel Types
// =====================================================================

export type ChannelType = 'web_widget' | 'phone' | 'sms' | 'whatsapp';

export interface ChannelConfig {
  web_widget: boolean;
  phone: boolean;
  sms: boolean;
  whatsapp: boolean;
}

export const DEFAULT_CHANNEL_CONFIG: ChannelConfig = {
  web_widget: true,
  phone: false,
  sms: false,
  whatsapp: false,
};

export const CHANNEL_OPTIONS: { value: ChannelType; label: string; description: string }[] = [
  { value: 'web_widget', label: 'Web Widget', description: 'Link to your genie\'s web page' },
  { value: 'phone', label: 'Phone Call', description: 'Direct phone number to call the genie' },
  { value: 'sms', label: 'SMS', description: 'Text message to start a conversation' },
  { value: 'whatsapp', label: 'WhatsApp', description: 'WhatsApp chat with the genie' },
];

// =====================================================================
// Touchpoint Types
// =====================================================================

export type TouchpointType =
  | 'qr_sticker'
  | 'email_signature'
  | 'social_card';

export interface TouchpointConfig {
  qr_sticker: boolean;
  email_signature: boolean;
  social_card: boolean;
}

export const DEFAULT_TOUCHPOINT_CONFIG: TouchpointConfig = {
  qr_sticker: true,
  email_signature: true,
  social_card: true,
};

export const TOUCHPOINT_OPTIONS: { value: TouchpointType; label: string; description: string; category: 'physical' | 'digital' }[] = [
  { value: 'qr_sticker', label: 'QR Code Sticker', description: 'Weather-resistant stickers for trucks, equipment, storefronts', category: 'physical' },
  { value: 'email_signature', label: 'Email Signature', description: 'HTML signature block with genie link', category: 'digital' },
  { value: 'social_card', label: 'Social Card', description: 'Shareable image for social media profiles', category: 'digital' },
];

// =====================================================================
// Asset Configuration
// =====================================================================

export type StickerSize = '2x2' | '3x3' | '4x4' | 'custom';
export type StickerShape = 'square' | 'rounded' | 'circle';
export type CardOrientation = 'portrait' | 'landscape';
export type SocialPlatform = 'facebook' | 'instagram' | 'linkedin' | 'twitter';

export interface AssetConfig {
  // Sticker options
  sticker_size?: StickerSize;
  sticker_shape?: StickerShape;
  sticker_custom_width?: number;
  sticker_custom_height?: number;

  // Business card options
  card_orientation?: CardOrientation;
  include_phone_number?: boolean;
  include_website_url?: boolean;

  // QR code options
  qr_style?: QRDisplayStyle;
  qr_label?: QRLabelOption;
  qr_include_logo?: boolean;

  /**
   * Catalogue product (marketplace_products.slug) whose Manual 2.0 page the
   * pack's product QR opens. Only a marketplace genie's pack can set one.
   */
  product_slug?: string | null;
  product_name?: string | null;

  // Social card options
  social_platform?: SocialPlatform;

  // Tagline for materials
  tagline?: string;
}

export const DEFAULT_ASSET_CONFIG: AssetConfig = {
  sticker_size: '3x3',
  sticker_shape: 'rounded',
  card_orientation: 'landscape',
  include_phone_number: true,
  include_website_url: true,
  qr_style: 'framed',
  qr_label: 'scan-to-connect',
  qr_include_logo: true,
  social_platform: 'linkedin',
  tagline: 'Scan to talk to our assistant',
};

// =====================================================================
// Promo Content
// =====================================================================

export type PromoTemplateType =
  | 'launch_email'
  | 'social_post'
  | 'customer_sms'
  | 'team_announcement'
  | 'website_banner';

export type PromoTone = 'professional' | 'friendly' | 'punchy';

export interface PromoTemplate {
  body: string;
  subject?: string;
  generated_at: string;
}

export type PromoContent = Partial<
  Record<PromoTemplateType, Partial<Record<PromoTone, PromoTemplate>>>
>;

export const PROMO_TEMPLATE_OPTIONS: {
  value: PromoTemplateType;
  label: string;
  description: string;
  hasSubject: boolean;
}[] = [
  {
    value: 'launch_email',
    label: 'Launch Email',
    description: 'Announce your Genie to customers and prospects',
    hasSubject: true,
  },
  {
    value: 'social_post',
    label: 'Social Post',
    description: 'LinkedIn or Facebook post to share with your network',
    hasSubject: false,
  },
  {
    value: 'customer_sms',
    label: 'Customer SMS',
    description: 'Short text to let existing customers know',
    hasSubject: false,
  },
  {
    value: 'team_announcement',
    label: 'Team Announcement',
    description: 'Internal note for staff so they know how it works',
    hasSubject: true,
  },
  {
    value: 'website_banner',
    label: 'Website Banner',
    description: 'Short copy for a homepage strip or popup',
    hasSubject: false,
  },
];

export const PROMO_TONE_OPTIONS: { value: PromoTone; label: string }[] = [
  { value: 'professional', label: 'Professional' },
  { value: 'friendly', label: 'Friendly' },
  { value: 'punchy', label: 'Punchy' },
];

/**
 * The tone auto-generated for each promo template. Shared by the promote
 * section's auto-fire, the detail page's "still building" check, and the zip
 * bundler's promo-copy export so all three agree on the canonical variant.
 */
export const DEFAULT_PROMO_TONES: Record<PromoTemplateType, PromoTone> = {
  launch_email: 'friendly',
  social_post: 'friendly',
  customer_sms: 'friendly',
  team_announcement: 'professional',
  website_banner: 'punchy',
};

// =====================================================================
// Activation Pack
// =====================================================================

export interface ActivationPack {
  id: string;
  user_id: string;
  agent_id: string | null;
  portal_id: string | null;
  name: string;
  description: string | null;
  channels: ChannelConfig;
  touchpoints: TouchpointConfig;
  branding_overrides: Partial<GenieBranding> | null;
  /**
   * Per-genie words for the printed booklets. Null means nothing has been
   * edited, so the capability starter kit applies — see `PackKitCopy` and
   * `resolvePackKit`. Reset writes null back rather than writing the preset
   * out, so an untouched genie keeps following the preset as it improves.
   */
  print_kit: Partial<PackKitCopy> | null;
  asset_config: AssetConfig;
  promo_content: PromoContent;
  /** Promo-input version used to reject copy generated from stale target data. */
  promo_input_revision?: string;
  generated_assets: GeneratedAssetCache;
  /** Generator-input version used to reject artwork built from an old pack. */
  asset_input_revision?: string;
  /** Accepted-cache version used to make concurrent publications single-winner. */
  asset_cache_revision?: string;
  created_at: string;
  updated_at: string;
}

/**
 * Cached pre-generated asset references. URL-backed assets (PNG/SVG/PDF) live
 * in the `activation-pack-assets` storage bucket. Text-only assets (email
 * signature HTML, embed code) inline their `data` string.
 */
export interface CachedAssetEntry {
  /** Public storage URL for binary assets. */
  url?: string;
  /** Inline content for text assets (HTML, etc). */
  data?: string;
  filename: string;
  mimeType: string;
  generated_at: string;
}

export type GeneratedAssetCache = Partial<Record<GeneratedAssetType, CachedAssetEntry>>;

export interface ActivationPackWithTarget extends ActivationPack {
  agent?: {
    id: string;
    name: string;
    url_name?: string | null;
    branding: GenieBranding | null;
    page: { url_name: string | null } | null;
    phone: { phone_number: string } | null;
  } | null;
  portal?: {
    id: string;
    name: string;
    slug: string;
    branding: GenieBranding | null;
  } | null;
}

export interface PackEmailSignatureSectionProps {
  pack: ActivationPackWithTarget;
  brand?: string;
}

// =====================================================================
// Public Activation DTO (microsite / smart link)
// =====================================================================

/** A single contact channel with its resolved public destination. */
export interface PublicActivationChannel {
  type: ChannelType;
  label: string;
  url: string;
}

/**
 * The ONLY shape exposed on the public, unauthenticated activation routes
 * (`/activate/:packId`, `/get/:packId`). Built explicitly by the public API
 * handler from whitelisted fields — never the raw pack row. No owner data,
 * no raw promo drafts, no storage internals.
 */
export interface PublicActivationDTO {
  id: string;
  /** Display name of the Genie/portal this pack activates. */
  name: string;
  tagline: string | null;
  /** Public description authored for the Genie/portal. Never an internal prompt. */
  description: string | null;
  /** Public-facing business or brand name, when the owner has supplied one. */
  businessName: string | null;
  /** The Genie's configured job. Null for portals and older records. */
  purpose: string | null;
  /** Public audience label configured on the Genie's page. */
  audience: string | null;
  /** Public greeting configured for the Genie/portal. */
  welcomeMessage: string | null;
  targetType: 'genie' | 'portal';
  branding: {
    primaryColor: string;
    secondaryColor: string | null;
    logoUrl: string | null;
    brandName: string | null;
    gradientEnabled: boolean;
    gradientAngle: number | null;
  };
  /** Primary web destination (live-agent page / portal page). */
  targetUrl: string;
  /** Enabled contact channels with resolved destinations. */
  channels: PublicActivationChannel[];
  /** Short, public-safe display copy (e.g. website banner). */
  promoTagline: string | null;
}

/** A purpose-safe benefit or step used by the public activation presentation. */
export interface ActivationPresentationCard {
  genieIcon: GenieIconName;
  title: string;
  body: string;
}

/** Purpose-specific copy that is safe to claim from the configured job alone. */
export interface ActivationPurposePresentation {
  label: string;
  promiseVerb: string;
  fallbackDescription: string;
  capabilities: ActivationPresentationCard[];
  examplePrompts: string[];
}

/**
 * Deterministic public copy resolved from the target's whitelisted context.
 * Keeping this separate from the DTO makes unsupported capability claims easy
 * to audit and test.
 */
export interface ActivationPresentationContent {
  heroEyebrow: string;
  statusLabel: string;
  promiseHeadline: string;
  promiseBody: string;
  highlights: string[];
  capabilityHeadline: string;
  capabilities: ActivationPresentationCard[];
  examplesEyebrow: string;
  examplesHeadline: string;
  examplePrompts: string[];
  welcomeMessage: string | null;
  examplesFootnote: string;
  stepsHeadline: string;
  steps: ActivationPresentationCard[];
  channelsHeadline: string;
  primaryActionLabel: string;
  finaleHeadline: string;
  finaleBody: string;
  appBody: string;
}

// =====================================================================
// Create/Update Params
// =====================================================================

export interface CreateActivationPackParams {
  name: string;
  agent_id?: string;
  portal_id?: string;
  description?: string;
  channels?: Partial<ChannelConfig>;
  touchpoints?: Partial<TouchpointConfig>;
  branding_overrides?: Partial<GenieBranding>;
  asset_config?: Partial<AssetConfig>;
  /**
   * Frontend origin used by the backend to derive the target URL for
   * pre-generated promo copy. Not persisted. Defaults to https://helpgenie.ai
   * on the server when omitted.
   */
  baseUrl?: string;
}

export interface UpdateActivationPackParams {
  name?: string;
  description?: string;
  channels?: Partial<ChannelConfig>;
  touchpoints?: Partial<TouchpointConfig>;
  branding_overrides?: Partial<GenieBranding> | null;
  /** Null resets the printed copy to the genie's capability starter kit. */
  print_kit?: Partial<PackKitCopy> | null;
  asset_config?: Partial<AssetConfig>;
  generated_assets?: GeneratedAssetCache;
}

// =====================================================================
// Generated Assets
// =====================================================================

export type GeneratedAssetType =
  | 'qr_png'
  | 'qr_svg'
  | 'product_qr_png'
  | 'product_qr_svg'
  | 'sticker_pdf'
  | 'email_signature_html'
  | 'social_card_png'
  | 'embed_code'
  | 'lookbook_pdf'
  | 'app_poster_png'
  | 'app_poster_pdf'
  | 'poster_pack_pdf';

export interface GeneratedAsset {
  type: GeneratedAssetType;
  label: string;
  description: string;
  data: string | Blob;
  filename: string;
  mimeType: string;
}

export const ASSET_TYPE_INFO: Record<GeneratedAssetType, { label: string; description: string; extension: string; mimeType: string }> = {
  qr_png: { label: 'QR Code (PNG)', description: 'High-resolution QR code image', extension: 'png', mimeType: 'image/png' },
  qr_svg: { label: 'QR Code (SVG)', description: 'Scalable QR code for any size', extension: 'svg', mimeType: 'image/svg+xml' },
  product_qr_png: { label: 'Product Manual QR (PNG)', description: 'Opens the product\'s manual page, answers cited from the official manual', extension: 'png', mimeType: 'image/png' },
  product_qr_svg: { label: 'Product Manual QR (SVG)', description: 'Scalable product manual QR for packaging and print', extension: 'svg', mimeType: 'image/svg+xml' },
  sticker_pdf: { label: 'Sticker Design', description: 'Print-ready sticker with QR and branding', extension: 'pdf', mimeType: 'application/pdf' },
  email_signature_html: { label: 'Email Signature', description: 'HTML code for email clients', extension: 'html', mimeType: 'text/html' },
  social_card_png: { label: 'Social Card', description: 'Optimized image for social profiles', extension: 'png', mimeType: 'image/png' },
  embed_code: { label: 'Embed Code', description: 'HTML snippet for your website', extension: 'html', mimeType: 'text/html' },
  lookbook_pdf: { label: 'Lookbook', description: 'Branded multi-page PDF showing your Genie out in the wild', extension: 'pdf', mimeType: 'application/pdf' },
  app_poster_png: { label: 'App Poster', description: 'Scan-to-get-started poster for screens and socials', extension: 'png', mimeType: 'image/png' },
  app_poster_pdf: { label: 'App Poster (Print)', description: 'Print-ready scan-to-get-started poster', extension: 'pdf', mimeType: 'application/pdf' },
  poster_pack_pdf: { label: 'Posters', description: 'Branded, print-ready poster designs for the wall, storefront, or staff room', extension: 'pdf', mimeType: 'application/pdf' },
};
